
import React, { useState } from 'react';
import { 
  Calendar, 
  Download, 
  TrendingUp, 
  Package, 
  DollarSign,
  Copy,
  ArrowUpDown,
  Building,
  FileText,
  BarChart3,
  Table as TableIcon,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { mockDashboardStats } from '@/api/mockData';

const Reports = () => {
  const [dateRange, setDateRange] = useState('7days');
  const [selectedHub, setSelectedHub] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('thisMonth');
  const [hoveredField, setHoveredField] = useState(null);
  const stats = mockDashboardStats;

  // Enhanced table data with more detailed information
  const tableData = [
    {
      date: '12/07/2025',
      orders: 43,
      deliveredOrders: 41,
      revenue: 2210000,
      successRate: 95.3,
      codAmount: 1680000,
      codCollected: 1595000,
      hub: 'Bưu cục Trung tâm',
      status: 'Hoạt động'
    },
    {
      date: '11/07/2025',
      orders: 55,
      deliveredOrders: 52,
      revenue: 2680000,
      successRate: 94.5,
      codAmount: 2150000,
      codCollected: 2030000,
      hub: 'Bưu cục Trung tâm',
      status: 'Hoạt động'
    },
    {
      date: '10/07/2025',
      orders: 61,
      deliveredOrders: 59,
      revenue: 2980000,
      successRate: 96.7,
      codAmount: 2340000,
      codCollected: 2260000,
      hub: 'Chi nhánh miền Nam',
      status: 'Hoạt động'
    },
    {
      date: '09/07/2025',
      orders: 38,
      deliveredOrders: 35,
      revenue: 1890000,
      successRate: 92.1,
      codAmount: 1450000,
      codCollected: 1335000,
      hub: 'Chi nhánh miền Bắc',
      status: 'Hoạt động'
    }
  ];

  const hubList = [
    { id: 'all', name: 'Tất cả bưu cục' },
    { id: 'center', name: 'Bưu cục Trung tâm' },
    { id: 'south', name: 'Chi nhánh miền Nam' },
    { id: 'north', name: 'Chi nhánh miền Bắc' }
  ];

  const exportToExcel = () => {
    console.log('Exporting to Excel...', { dateRange, selectedHub });
    alert('Đang xuất báo cáo Excel. File sẽ bao gồm dữ liệu chi tiết theo bưu cục và khoảng thời gian đã chọn.');
  };

  const exportToPDF = () => {
    console.log('Exporting to PDF...', { dateRange, selectedHub });
    alert('Đang xuất báo cáo PDF cho họp nội bộ. File sẽ chứa biểu đồ và bảng dữ liệu được lọc.');
  };

  const getFilteredData = () => {
    if (selectedHub === 'all') return tableData;
    return tableData.filter(item => {
      switch (selectedHub) {
        case 'center': return item.hub === 'Bưu cục Trung tâm';
        case 'south': return item.hub === 'Chi nhánh miền Nam';
        case 'north': return item.hub === 'Chi nhánh miền Bắc';
        default: return true;
      }
    });
  };

  const filteredData = getFilteredData();
  const totalRevenue = filteredData.reduce((sum, item) => sum + item.revenue, 0);
  const totalOrders = filteredData.reduce((sum, item) => sum + item.orders, 0);
  const totalCOD = filteredData.reduce((sum, item) => sum + item.codCollected, 0);
  const avgSuccessRate = filteredData.reduce((sum, item) => sum + item.successRate, 0) / filteredData.length;

  // Copy function
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Đã sao chép vào clipboard!");
    } catch (err) {
      console.error('Failed to copy: ', err);
      alert("Không thể sao chép");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Báo cáo & Phân tích</h1>
        <div className="flex space-x-2">
          <Select value={selectedHub} onValueChange={setSelectedHub}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Chọn bưu cục" />
            </SelectTrigger>
            <SelectContent>
              {hubList.map((hub) => (
                <SelectItem key={hub.id} value={hub.id}>
                  {hub.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Thời gian" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hôm nay</SelectItem>
              <SelectItem value="7days">7 ngày qua</SelectItem>
              <SelectItem value="30days">30 ngày qua</SelectItem>
              <SelectItem value="thisweek">Tuần này</SelectItem>
              <SelectItem value="thismonth">Tháng này</SelectItem>
              <SelectItem value="custom">Tùy chỉnh</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700">
            <Download className="h-4 w-4 mr-2" />
            Xuất Excel
          </Button>
          <Button onClick={exportToPDF} variant="outline">
            <FileText className="h-4 w-4 mr-2" />
            Xuất PDF
          </Button>
        </div>
      </div>

      {/* Enhanced Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100">Tổng đơn hàng</p>
                <p className="text-3xl font-bold">{totalOrders.toLocaleString()}</p>
                <p className="text-sm text-blue-200 mt-1">
                  {selectedHub !== 'all' ? hubList.find(h => h.id === selectedHub)?.name : 'Tất cả bưu cục'}
                </p>
              </div>
              <BarChart3 className="h-12 w-12 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100">Doanh thu</p>
                <p className="text-3xl font-bold">{(totalRevenue / 1000000).toFixed(1)}M</p>
                <p className="text-sm text-green-200 mt-1">
                  {totalRevenue.toLocaleString()} VNĐ
                </p>
              </div>
              <TrendingUp className="h-12 w-12 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100">Thu COD</p>
                <p className="text-3xl font-bold">{(totalCOD / 1000000).toFixed(1)}M</p>
                <p className="text-sm text-purple-200 mt-1">
                  {totalCOD.toLocaleString()} VNĐ
                </p>
              </div>
              <Calendar className="h-12 w-12 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100">Tỷ lệ thành công</p>
                <p className="text-3xl font-bold">{avgSuccessRate.toFixed(1)}%</p>
                <p className="text-sm text-orange-200 mt-1">
                  Trung bình khu vực
                </p>
              </div>
              <TrendingUp className="h-12 w-12 text-orange-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="charts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="charts" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Biểu đồ phân tích
          </TabsTrigger>
          <TabsTrigger value="table" className="flex items-center gap-2">
            <TableIcon className="h-4 w-4" />
            Dữ liệu chi tiết
          </TabsTrigger>
          <TabsTrigger value="performance" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Hiệu suất vận hành
          </TabsTrigger>
        </TabsList>

        <TabsContent value="charts">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Xu hướng doanh thu theo ngày</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={filteredData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'revenue' ? `${((Number(value) || 0) / 1000000).toFixed(1)}M VNĐ` : value,
                        name === 'revenue' ? 'Doanh thu' : 'Đơn hàng'
                      ]}
                    />
                    <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} />
                    <Line type="monotone" dataKey="orders" stroke="#3b82f6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* COD Collection Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Thu COD và Tỷ lệ thu thập</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={filteredData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${((Number(value) || 0) / 1000000).toFixed(1)}M VNĐ`, 'COD']} />
                    <Bar dataKey="codAmount" fill="#f59e0b" name="COD cần thu" />
                    <Bar dataKey="codCollected" fill="#10b981" name="COD đã thu" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Success Rate Trend */}
            <Card>
              <CardHeader>
                <CardTitle>Tỷ lệ giao hàng thành công</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={filteredData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[85, 100]} />
                    <Tooltip formatter={(value) => [`${value}%`, 'Tỷ lệ thành công']} />
                    <Line type="monotone" dataKey="successRate" stroke="#dc2626" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Hub Performance Comparison */}
            <Card>
              <CardHeader>
                <CardTitle>So sánh hiệu suất bưu cục</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={tableData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hub" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="orders" fill="#3b82f6" name="Đơn hàng" />
                    <Bar dataKey="deliveredOrders" fill="#10b981" name="Giao thành công" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="table">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Bảng dữ liệu chi tiết vận hành</CardTitle>
                <div className="flex space-x-2">
                  <Button onClick={exportToExcel} size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Xuất Excel
                  </Button>
                  <Button onClick={exportToPDF} size="sm" variant="outline">
                    <FileText className="h-4 w-4 mr-2" />
                    Xuất PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[16em]">
                        <div className="flex items-center gap-2">
                          Ngày
                          <ArrowUpDown className="h-4 w-4 cursor-pointer" />
                        </div>
                      </TableHead>
                      <TableHead className="min-w-[16em]">Bưu cục</TableHead>
                      <TableHead className="min-w-[16em]">Tổng đơn</TableHead>
                      <TableHead className="min-w-[16em]">Giao thành công</TableHead>
                      <TableHead className="min-w-[16em]">Tỷ lệ (%)</TableHead>
                      <TableHead className="min-w-[16em]">Doanh thu (VNĐ)</TableHead>
                      <TableHead className="min-w-[16em]">COD cần thu</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{row.date}</TableCell>
                        <TableCell>{row.hub}</TableCell>
                        <TableCell>{row.orders}</TableCell>
                        <TableCell>{row.deliveredOrders}</TableCell>
                        <TableCell>
                          <span className={`font-medium ${
                            row.successRate >= 95 ? 'text-green-600' : 
                            row.successRate >= 90 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {row.successRate}%
                          </span>
                        </TableCell>
                        <TableCell className="font-medium text-primary">
                          {row.revenue.toLocaleString()}
                        </TableCell>
                        <TableCell className="font-medium text-orange-600">
                          {row.codAmount.toLocaleString()}
                        </TableCell>
                        <TableCell className="font-medium text-green-600">
                          {row.codCollected.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                            {row.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Hiệu suất tài chính</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                    <span className="font-medium">Tổng doanh thu kỳ này</span>
                    <span className="text-2xl font-bold text-blue-600">
                      {(totalRevenue / 1000000).toFixed(1)}M VNĐ
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                    <span className="font-medium">COD thu được</span>
                    <span className="text-2xl font-bold text-green-600">
                      {((totalCOD / totalCOD) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                    <span className="font-medium">Tăng trưởng dự kiến</span>
                    <span className="text-2xl font-bold text-purple-600">+15-20%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Chỉ số vận hành quan trọng</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-orange-50 rounded-lg">
                    <span className="font-medium">Thời gian giao hàng trung bình</span>
                    <span className="text-xl font-bold text-orange-600">24-48h</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-red-50 rounded-lg">
                    <span className="font-medium">Tỷ lệ khiếu nại</span>
                    <span className="text-xl font-bold text-red-600">&lt; 2%</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-indigo-50 rounded-lg">
                    <span className="font-medium">Độ hài lòng khách hàng</span>
                    <span className="text-xl font-bold text-indigo-600">4.5/5</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
